import React, { useState } from 'react';

const PERIODS = [
  { key: 'Curr Qtr', label: 'Current Quarter' },
  { key: 'Next Qtr', label: 'Next Quarter' },
  { key: 'Curr Yr', label: 'Current Year' },
  { key: 'Next Yr', label: 'Next Year' },
];

function ResearchEPSTrendsTable({ data, onTickerClick }) {
  const [viewMode, setViewMode] = useState('heatmap');

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const calculateTrendPercent = (row, periodKey) => {
    const values = [
      row[`${periodKey} 90`],
      row[`${periodKey} 60`],
      row[`${periodKey} 30`],
      row[`${periodKey} 7`],
      row[`${periodKey} Curr`],
    ];
    const valid = values.filter(v => v != null && !isNaN(v));
    if (valid.length < 2) return null;
    const first = valid[0];
    const last = valid[valid.length - 1];
    if (first === 0) return null;
    return ((last - first) / Math.abs(first)) * 100;
  };

  const getTrendArrow = (change) => {
    if (change == null || Math.abs(change) < 0.1) return { arrow: '\u2192', color: 'text-gray-400' };
    if (change > 0) return { arrow: '\u2197', color: 'text-green-400' };
    return { arrow: '\u2198', color: 'text-red-400' };
  };

  const formatTrendPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const formatted = value.toFixed(1);
    return value > 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Heatmap color logic
  const allTrends = data.flatMap(row =>
    PERIODS.map(p => calculateTrendPercent(row, p.key))
  ).filter(v => v != null && !isNaN(v));
  const maxAbsVal = allTrends.length > 0
    ? Math.max(Math.abs(Math.min(...allTrends)), Math.abs(Math.max(...allTrends)), 5)
    : 5;

  const getColorClass = (value) => {
    if (value == null || isNaN(value)) return 'bg-gray-600';
    const intensity = Math.abs(value) / maxAbsVal;
    if (value > 0) {
      if (intensity < 0.2) return 'bg-green-900/40';
      if (intensity < 0.4) return 'bg-green-800/60';
      if (intensity < 0.6) return 'bg-green-700/80';
      if (intensity < 0.8) return 'bg-green-600';
      return 'bg-green-500';
    } else {
      if (intensity < 0.2) return 'bg-red-900/40';
      if (intensity < 0.4) return 'bg-red-800/60';
      if (intensity < 0.6) return 'bg-red-700/80';
      if (intensity < 0.8) return 'bg-red-600';
      return 'bg-red-500';
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No EPS trends data available. Run an analysis from the Analysis Input tab.</p>
      </div>
    );
  }

  const tickers = data.map(row => row.Ticker).filter(Boolean);

  // Build heatmap data: rows = periods, columns = tickers
  const heatmapData = PERIODS.map(period => ({
    period,
    values: tickers.map(ticker => {
      const row = data.find(r => r.Ticker === ticker);
      return row ? calculateTrendPercent(row, period.key) : null;
    }),
    rawData: tickers.map(ticker => {
      const row = data.find(r => r.Ticker === ticker);
      return row ? {
        start: row[`${period.key} 90`],
        current: row[`${period.key} Curr`],
        ticker
      } : null;
    })
  }));

  const getTooltipContent = (ticker, period, value, rawData) => {
    if (!rawData || value == null) return `${ticker} ${period}: No data`;
    const change = rawData.current - rawData.start;
    const changeSymbol = change >= 0 ? '+' : '';
    return `${ticker} - ${period}\n90d ago: ${formatCurrency(rawData.start)}\nCurrent: ${formatCurrency(rawData.current)}\nChange: ${changeSymbol}${formatCurrency(change)} (${formatTrendPercent(value)})`;
  };

  const renderHeatmapView = () => (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white mb-2">EPS Trend Slopes (90d → Current)</h3>
        <p className="text-sm text-gray-400">
          Heatmap showing the slope of EPS estimate changes over time.
          <span className="text-green-400 ml-2">Green = Improving estimates</span>
          <span className="text-red-400 ml-2">Red = Declining estimates</span>
        </p>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Header with tickers */}
          <div className="grid gap-px mb-1" style={{ gridTemplateColumns: `120px repeat(${tickers.length}, 80px)` }}>
            <div className="bg-gray-700 p-2 text-sm font-medium text-gray-300 text-center">
              Period
            </div>
            {tickers.map((ticker) => (
              <div key={ticker} className="bg-gray-700 p-2 text-xs font-medium text-center">
                <div
                  className="text-blue-400 hover:text-blue-300 hover:underline cursor-pointer"
                  onClick={() => onTickerClick && onTickerClick(ticker)}
                >
                  {ticker}
                </div>
              </div>
            ))}
          </div>

          {/* Heatmap rows */}
          {heatmapData.map(({ period, values, rawData }) => (
            <div key={period.key} className="grid gap-px mb-1" style={{ gridTemplateColumns: `120px repeat(${tickers.length}, 80px)` }}>
              <div className="bg-gray-700 p-2 text-sm text-gray-300 flex items-center">
                <span className="font-medium">{period.key} Trend</span>
              </div>
              {values.map((value, index) => {
                const trendInfo = getTrendArrow(value);
                return (
                  <div
                    key={index}
                    className={`p-2 text-xs text-center transition-all hover:scale-105 hover:z-10 relative cursor-help ${getColorClass(value)}`}
                    title={getTooltipContent(tickers[index], period.key, value, rawData[index])}
                  >
                    <div className="text-white font-mono text-xs leading-tight">
                      {formatTrendPercent(value)}
                    </div>
                    <div className={`text-xs mt-1 ${trendInfo.color}`}>
                      {value != null && !isNaN(value) ? trendInfo.arrow : '?'}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-6 text-xs text-gray-400">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded"></div>
          <span>Strong Decline</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-800 rounded"></div>
          <span>Decline</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-gray-600 rounded"></div>
          <span>Neutral</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-800 rounded"></div>
          <span>Improvement</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span>Strong Improvement</span>
        </div>
      </div>
    </div>
  );

  const renderDetailedView = () => (
    <div className="space-y-6">
      {data.map((row, stockIndex) => (
        <div key={stockIndex} className="bg-gray-800 rounded-lg p-6">
          {/* Stock Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-white">
              <span
                className="cursor-pointer text-blue-400 hover:text-blue-300 hover:underline"
                onClick={() => onTickerClick && onTickerClick(row.Ticker)}
              >
                {row.Ticker}
              </span>
              {' '}- EPS Estimate Timeline
            </h3>
          </div>

          {/* Timeline Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-600 rounded-lg">
              <thead>
                <tr style={{ backgroundColor: '#1a1a1a' }}>
                  <th className="text-left py-3 px-4 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>Period</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>90d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>60d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>30d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>7d Ago</th>
                  <th className="text-center py-3 px-3 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>Current</th>
                  <th className="text-center py-3 px-4 font-medium" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {PERIODS.map((period, periodIndex) => {
                  const percentChange = calculateTrendPercent(row, period.key);
                  const trend = getTrendArrow(percentChange);

                  return (
                    <tr key={period.key} className={`border-t border-gray-600 ${periodIndex % 2 === 0 ? 'bg-gray-800/50' : ''} hover:bg-[rgba(33,138,255,0.15)] transition-colors`}>
                      <td className="py-3 px-4 font-medium text-white">
                        {period.label}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 90`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 60`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 30`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white">
                        {formatCurrency(row[`${period.key} 7`])}
                      </td>
                      <td className="py-3 px-3 text-center text-white font-semibold">
                        {formatCurrency(row[`${period.key} Curr`])}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <span className={`text-lg ${trend.color}`}>{trend.arrow}</span>
                          {percentChange != null && (
                            <span className={`text-sm font-medium ${trend.color}`}>
                              {percentChange > 0 ? '+' : ''}{percentChange.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary Stats */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {PERIODS.map((period) => {
              const percentChange = calculateTrendPercent(row, period.key);
              const trend = getTrendArrow(percentChange);

              return (
                <div key={period.key} className="bg-gray-700/50 rounded p-3">
                  <div className="text-gray-400 mb-1">{period.label} Trend</div>
                  <div className={`text-lg font-semibold ${trend.color}`}>
                    {trend.arrow} {percentChange != null ? `${percentChange.toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {row[`${period.key} 90`] != null && row[`${period.key} Curr`] != null
                      ? `${formatCurrency(row[`${period.key} 90`])} \u2192 ${formatCurrency(row[`${period.key} Curr`])}`
                      : 'Insufficient data'
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="text-sm text-gray-400 text-center">
        Showing detailed EPS estimate timeline for {data.length} {data.length === 1 ? 'stock' : 'stocks'}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex justify-end">
        <div className="flex bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('heatmap')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'heatmap'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Heatmap View
          </button>
          <button
            onClick={() => setViewMode('detailed')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'detailed'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Detailed View
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'heatmap' ? renderHeatmapView() : renderDetailedView()}
    </div>
  );
}

export default ResearchEPSTrendsTable;
